import {
	Button,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@medme/ui"
import { Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import type { Lead } from "@/shared/api/generated/types/marketing"
import { LEAD_STATUS_CLASSES, LEAD_STATUS_KEYS } from "../model/constants"
import {
	formatLeadDate,
	formatLeadPhone,
	formatLeadSource,
	formatLeadUtmShort,
} from "../model/formatters"

type LeadsTableProps = {
	leads: Lead[]
	isPending: boolean
	isError: boolean
	onRetry: () => void
	onResetFilters: () => void
	onOpenLead: (lead: Lead) => void
}

export function LeadsTable({
	leads,
	isPending,
	isError,
	onRetry,
	onResetFilters,
	onOpenLead,
}: LeadsTableProps) {
	const { t, i18n } = useTranslation()

	if (isPending) {
		return (
			<output className="flex justify-center p-12">
				<Loader2 className="animate-spin" />
				<span className="sr-only">{t("leads.loading")}</span>
			</output>
		)
	}
	if (isError) {
		return (
			<div className="p-12 text-center" role="alert">
				<p>{t("leads.error.load")}</p>
				<Button
					className="mt-3 h-auto py-2"
					variant="outline"
					onClick={onRetry}
				>
					{t("leads.actions.retry")}
				</Button>
			</div>
		)
	}
	if (leads.length === 0) {
		return (
			<output aria-live="polite" className="block p-12 text-center">
				<p>{t("leads.empty.title")}</p>
				<Button
					className="mt-3 h-auto py-2"
					variant="outline"
					onClick={onResetFilters}
				>
					{t("leads.actions.resetFilters")}
				</Button>
			</output>
		)
	}

	return (
		<Table className="block w-full xl:table">
			<TableHeader className="hidden bg-mm-muted-2 xl:table-header-group">
				<TableRow>
					<TableHead className="h-auto whitespace-normal px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-mm-muted-fg">
						{t("leads.table.phone")}
					</TableHead>
					<TableHead className="h-auto whitespace-normal px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-mm-muted-fg">
						{t("leads.table.status")}
					</TableHead>
					<TableHead className="h-auto whitespace-normal px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-mm-muted-fg">
						{t("leads.table.source")}
					</TableHead>
					<TableHead className="h-auto whitespace-normal px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-mm-muted-fg">
						{t("leads.table.utm")}
					</TableHead>
					<TableHead className="h-auto whitespace-normal px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-mm-muted-fg">
						{t("leads.table.domain")}
					</TableHead>
					<TableHead className="h-auto whitespace-normal px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-mm-muted-fg">
						{t("leads.table.createdAt")}
					</TableHead>
					<TableHead className="h-auto whitespace-normal px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-mm-muted-fg">
						<span className="sr-only">{t("leads.table.action")}</span>
					</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 xl:table-row-group xl:p-0">
				{leads.map((lead) => (
					<TableRow
						key={lead.id}
						className="grid grid-cols-2 gap-4 rounded-xl border bg-mm-card p-4 hover:bg-mm-muted-2 xl:table-row xl:rounded-none xl:border-x-0 xl:border-t-0 xl:p-0"
					>
						<TableCell className="block break-words whitespace-normal p-0 font-medium xl:table-cell xl:px-4 xl:py-4">
							<span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-mm-muted-fg xl:hidden">
								{t("leads.table.phone")}
							</span>
							{formatLeadPhone(lead.phone, lead.phoneNormalized)}
						</TableCell>
						<TableCell className="block whitespace-normal p-0 xl:table-cell xl:px-4 xl:py-4">
							<span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-mm-muted-fg xl:hidden">
								{t("leads.table.status")}
							</span>
							<span
								className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${LEAD_STATUS_CLASSES[lead.status]}`}
							>
								<span aria-hidden="true">●</span>
								{t(LEAD_STATUS_KEYS[lead.status])}
							</span>
						</TableCell>
						<TableCell className="block break-words whitespace-normal p-0 text-mm-muted-fg xl:table-cell xl:px-4 xl:py-4">
							<span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-mm-muted-fg xl:hidden">
								{t("leads.table.source")}
							</span>
							{formatLeadSource(lead.source)}
						</TableCell>
						<TableCell className="block break-words whitespace-normal p-0 text-mm-muted-fg xl:table-cell xl:px-4 xl:py-4">
							<span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-mm-muted-fg xl:hidden">
								{t("leads.table.utm")}
							</span>
							{formatLeadUtmShort(lead)}
						</TableCell>
						<TableCell className="block break-all whitespace-normal p-0 text-mm-muted-fg xl:table-cell xl:px-4 xl:py-4">
							<span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-mm-muted-fg xl:hidden">
								{t("leads.table.domain")}
							</span>
							{lead.domain}
						</TableCell>
						<TableCell className="block break-words whitespace-normal p-0 text-mm-muted-fg xl:table-cell xl:px-4 xl:py-4">
							<span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-mm-muted-fg xl:hidden">
								{t("leads.table.createdAt")}
							</span>
							{formatLeadDate(lead.createdAt, i18n.language)}
						</TableCell>
						<TableCell className="col-span-2 block whitespace-normal p-0 xl:table-cell xl:px-4 xl:py-4">
							<Button
								className="h-auto w-full rounded-[10px] py-2 xl:w-auto"
								size="sm"
								variant="outline"
								onClick={() => onOpenLead(lead)}
							>
								{t("leads.actions.open")}
							</Button>
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	)
}
